import React, { useMemo, useCallback, useEffect, useState, useRef } from "react";
import { useSafeIsMobile } from "app/utils/deviceDetection";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  useDisclosure,
  Select,
  SelectItem,
  SelectSection,
  Selection,
} from "@heroui/react";
import { IoMdCheckmarkCircle } from "react-icons/io";
import { MdCancel } from "react-icons/md";
import { getColorHex, colorHex } from "app/utils/colorName";

// -----------------------------------------------------------------------------
// Type definitions
// -----------------------------------------------------------------------------
interface Color {
  _id: { $oid: string };
  name: string;
  colour?: string;
  swatch?: string[];
}

interface StockData {
  headers: string[];
  web_columns: number[];
  stock: (string | number | null)[][];
  items?: {
    sku?: string;
    name?: string;
    colour?: string;
    size?: string;
    qty_on_hand?: string | number;
    qty_on_order?: string | number;
    date_expected?: string;
  }[];
}

interface ListData {
  list: Color[];
}

export type ColorStockData = {
  id: string;
  name: string;
  size: string;
  code: string;
  qtyOnHand: number;
  status: string;
};

export type StockItem = {
  id: string;
  sku?: string | null;
  description?: string | null;
  quantity: number;
  onOrder?: number | null;
  dueDate?: string | null;
  colour?: string | null;
  size?: string | null;
  dateExpected?: string | null;
};

// Props for StockDataV2 component
interface StockDataV2Props {
  slug: string;
  stockData: StockData;
  listData: ListData;
  getColors?: (colors: { name?: string; id: string }[]) => void;
  handleStockCheck: (inStock: boolean, color?: ColorStockData) => void;
}

// -----------------------------------------------------------------------------
// Utility functions
// -----------------------------------------------------------------------------
const parseQuantity = (value: unknown): number => {
  if (typeof value === "string") {
    const num = parseInt(value.replace(/[^0-9]/g, ""), 10);
    return isNaN(num) ? 0 : num;
  }
  return typeof value === "number" ? value : 0;
};

const normalizeColor = (name: string | null | undefined): string =>
  (name || "").toLowerCase().trim();

const getDefaultColorKeys = (listData: ListData): Set<string> =>
  new Set(
    (listData?.list || []).map((c) => `${c.name?.toLowerCase().trim()}-`.replace(/\s+/g, " ")),
  );

// Custom hook for debounced effects
function useDebouncedEffect(effect: () => void, deps: any[], delay: number) {
  const handler = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (handler.current) clearTimeout(handler.current);
    handler.current = setTimeout(effect, delay);

    return () => {
      if (handler.current) clearTimeout(handler.current);
    };
  }, deps);
}

// -----------------------------------------------------------------------------
// Stock Data Processor
// -----------------------------------------------------------------------------
class StockDataProcessor {
  private headers: string[];
  private stock: (string | number | null)[][];
  private items: StockData["items"];

  constructor(stockData: StockData) {
    this.headers = stockData.headers || [];
    this.stock = stockData.stock || [];
    this.items = stockData.items;
  }

  private buildHeaderMap() {
    const headerMap = {
      sku: this.headers.find((h) => h.toLowerCase().includes("sku")) || "SKU",
      size: this.headers.find((h) => h.toLowerCase().includes("size")) || "Size",
      description:
        this.headers.find(
          (h) => h.toLowerCase().includes("desc") || h.toLowerCase().includes("colour"),
        ) || "Desc",
      quantity:
        this.headers.find(
          (h) => h.toLowerCase().includes("qty") || h.toLowerCase().includes("quantity"),
        ) || "Quantity",
      onOrder: this.headers.find((h) => h.toLowerCase().includes("on order")) || "On order",
      dueDate: this.headers.find((h) => h.toLowerCase().includes("due date")) || "Due date",
      dateExpected: "Date Expected",
    };

    const headerIndexMap: Record<string, number> = {};
    this.headers.forEach((h, i) => {
      headerIndexMap[h.toLowerCase()] = i;
    });

    return { headerMap, headerIndexMap };
  }

  process(): StockItem[] {
    if (this.headers.length > 0 && this.stock.length > 0) {
      return this.processLegacyData();
    } else if (this.items && this.items.length > 0) {
      return this.processNewData();
    }
    return [];
  }

  private processLegacyData(): StockItem[] {
    const { headerMap, headerIndexMap } = this.buildHeaderMap();

    return this.stock.map((stockItem, index) => ({
      id: `${
        stockItem[headerIndexMap["sku"] ?? this.headers.indexOf(headerMap.sku)] || "sku"
      }-${index}`,
      sku: String(stockItem[headerIndexMap["sku"] ?? this.headers.indexOf(headerMap.sku)] || ""),
      description: String(
        stockItem[
          headerIndexMap["desc"] ??
            headerIndexMap["colour"] ??
            this.headers.indexOf(headerMap.description)
        ] || "",
      ),
      quantity: parseQuantity(
        stockItem[headerIndexMap["quantity"] ?? this.headers.indexOf(headerMap.quantity)],
      ),
      onOrder:
        stockItem[headerIndexMap["on order"] ?? this.headers.indexOf(headerMap.onOrder)] !==
        undefined
          ? parseQuantity(
              stockItem[headerIndexMap["on order"] ?? this.headers.indexOf(headerMap.onOrder)],
            )
          : null,
      dueDate: String(
        stockItem[headerIndexMap["due date"] ?? this.headers.indexOf(headerMap.dueDate)] || "",
      ),
      dateExpected: String(
        stockItem[
          headerIndexMap["date expected"] ?? this.headers.indexOf(headerMap.dateExpected)
        ] ||
          (Array.isArray(this.items) && this.items?.[index]?.date_expected) ||
          "",
      ),
      size:
        headerIndexMap["size"] !== undefined
          ? String(stockItem[headerIndexMap["size"]] || "")
          : null,
      colour: String(
        stockItem[
          headerIndexMap["desc"] ??
            headerIndexMap["colour"] ??
            this.headers.indexOf(headerMap.description)
        ] || "",
      ),
    }));
  }

  private processNewData(): StockItem[] {
    return (this.items || []).map((item, index) => ({
      id: `${item.colour || item.name}-${index}`,
      sku: item.sku || null,
      description:
        item.name || (item.colour ? `${item.colour} ${item?.size || ""}` : "special-order"),
      colour: item.colour || item.name || "special-order",
      size: item.size || null,
      quantity: parseQuantity(item.qty_on_hand),
      onOrder:
        item.qty_on_order !== undefined && item.qty_on_order !== null
          ? parseQuantity(item.qty_on_order)
          : null,
      dueDate: item.date_expected || null,
      dateExpected: item.date_expected || null,
    }));
  }
}

// -----------------------------------------------------------------------------
// Color Grouping Logic
// -----------------------------------------------------------------------------
const parseAdvancedColorKey = (str: string) => {
  const lower = str.toLowerCase();

  // Fast prefix checks for known patterns
  const patterns = [
    { prefix: "mba", regex: /^mba(\d+)\s*-\s*(.+)$/i },
    { prefix: "sh", regex: /^sh(\d+)\s(.+)\s(s|xs|m|l|xl)$/i },
    { prefix: "sh-basic", regex: /^sh(\d+)\s+(.+)$/i },
    { prefix: "gf", regex: /^gf(\d+)\s+(.+)$/i },
    { prefix: "kit", regex: /^kit(\d+)\s+(.+)$/i },
    { prefix: "cd", regex: /^cd(\d+)\s*-\s*(.+)$/i },
    { prefix: "cbd", regex: /^cbd(\d+)\s*-\s*(.+)$/i },
    { prefix: "nb", regex: /^nb(\d+)\s*-\s*(.+)$/i },
    { prefix: "color-size", regex: /^(.+)\s(s|m|l|xl)$/i },
    { prefix: "color-code", regex: /^(.+)\s(\d+[a-zA-Z]+)$/i },
    { prefix: "color-mens", regex: /^(.+)\s(mens?)\s(s|xs|m|l|xl)$/i },
    { prefix: "color-womens", regex: /^(.+)\s(womens?)\s(s|xs|m|l|xl)$/i },
  ];

  for (const pattern of patterns) {
    if (
      lower.startsWith(pattern.prefix) ||
      pattern.prefix === "color-size" ||
      pattern.prefix === "color-code" ||
      pattern.prefix === "color-mens" ||
      pattern.prefix === "color-womens"
    ) {
      const match = str.match(pattern.regex);
      if (match) {
        return { type: pattern.prefix, ...match.slice(1) };
      }
    }
  }

  return null;
};

const groupColors = (stockItems: StockItem[], listData: ListData) => {
  const colorHexKeys = new Set(Object.keys(colorHex).map((k) => k.toLowerCase()));
  const defaultKeys = getDefaultColorKeys(listData);

  const defaultColors: StockItem[] = [];
  const addonColors: StockItem[] = [];

  for (const item of stockItems) {
    const colorName = normalizeColor(item.colour);
    if (
      colorHexKeys.has(colorName) ||
      defaultKeys.has(`${colorName}-`) ||
      parseAdvancedColorKey(item.colour || "")
    ) {
      defaultColors.push(item);
    } else {
      addonColors.push(item);
    }
  }

  return { defaultColors, addonColors };
};

// -----------------------------------------------------------------------------
// Stock Item Display Components
// -----------------------------------------------------------------------------
const StockItemDisplay = React.memo<{
  item: StockItem;
  isSelected?: boolean;
  onClick?: () => void;
  showStockInfo?: boolean;
}>(({ item, isSelected, onClick, showStockInfo = true }) => {
  const stockStatus = item.quantity > 0 ? "In Stock" : "Out of Stock";
  const hasDueDate = item.dueDate && item.dueDate !== "-";
  const hasDateExpected = item.dateExpected && item.dateExpected !== "-";
  const hasOnOrder = item.onOrder && item.onOrder > 0;

  return (
    <div
      className={`flex gap-4 items-center justify-start p-2 rounded cursor-pointer transition-colors ${
        isSelected ? "bg-blue-50 dark:bg-blue-900/20" : " dark:hover:bg-gray-800"
      }`}
      onClick={onClick}
    >
      <div className="flex items-center min-w-32 w-44 gap-x-2.5">
        <div
          className="w-3 h-3 rounded-full border border-gray-50"
          style={{
            backgroundColor: getColorHex(item.colour?.toLowerCase() || "") || "",
          }}
        />
        <span className="font-medium">
          {item.colour} {item.size}
        </span>
      </div>

      {showStockInfo && (
        <div className="flex-1">
          <div className="flex items-center w-40 gap-x-1">
            {item.quantity > 0 && (
              <span className="text-sm text-foreground-800 font-medium">{item.quantity}</span>
            )}
            <span className="flex items-center text-sm">
              {item.quantity > 0 ? (
                <IoMdCheckmarkCircle className="text-green-600 mr-1" />
              ) : (
                <MdCancel className="text-red-600 mr-1" />
              )}
              {stockStatus}
            </span>
          </div>

          {hasDueDate || hasDateExpected || hasOnOrder ? (
            <div className="italic text-xs text-foreground-600 mt-1">
              {hasDueDate || hasDateExpected
                ? `More Due ${hasDueDate ? item.dueDate : item.dateExpected}`
                : `More Due`}
              {/* {item.onOrder} On Order */}
            </div>
          ) : (
            <> </>
          )}
        </div>
      )}
    </div>
  );
});

StockItemDisplay.displayName = "StockItemDisplay";

// -----------------------------------------------------------------------------
// Main Component
// -----------------------------------------------------------------------------
const StockDataV2: React.FC<StockDataV2Props> = ({
  slug,
  stockData,
  listData,
  handleStockCheck,
  getColors,
}) => {
  const isMobile = useSafeIsMobile();
  const [selectedId, setSelectedId] = useState<string>("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { isOpen, onOpen, onOpenChange } = useDisclosure();

  // Process stock data
  const processedStock = useMemo(() => {
    const processor = new StockDataProcessor(stockData);
    const stock = processor.process();

    // Fallback to listData if no stock data
    if (stock.length === 0 && listData?.list) {
      return listData.list.map((item, index) => ({
        id: `${item.name}-${index}`,
        sku: null,
        description: item.name,
        colour: item.name,
        size: null,
        quantity: 0,
        onOrder: null,
        dueDate: null,
        dateExpected: null,
      }));
    }

    return stock;
  }, [stockData, listData]);

  // Group colors
  const { defaultColors, addonColors } = useMemo(
    () => groupColors(processedStock, listData),
    [processedStock, listData],
  );

  // Create lookup map
  const stockMap = useMemo(() => {
    const map = new Map<string, StockItem>();
    processedStock.forEach((item) => map.set(item.id, item));
    return map;
  }, [processedStock]);

  // Selected item
  const selectedItem = useMemo(() => {
    return selectedId ? stockMap.get(selectedId) : null;
  }, [selectedId, stockMap]);

  // Memoized color data for parent callback
  const colorDataForParent = useMemo(() => {
    return processedStock.map((item) => ({
      name: `${item.colour} ${item.size || ""}`,
      id: item.id,
    }));
  }, [processedStock]);

  // Memoized stock check data
  const stockCheckData = useMemo(() => {
    if (!selectedItem) return null;

    return {
      id: `${selectedItem.colour}-${selectedItem.size}`,
      name: selectedItem.colour || "",
      size: selectedItem.size || "",
      code: selectedItem.colour?.toLowerCase() || "",
      qtyOnHand: selectedItem.quantity,
      status: selectedItem.quantity > 0 ? "In Stock" : "Out of Stock",
    };
  }, [selectedItem]);

  // Set default selection
  useEffect(() => {
    if (processedStock.length > 0 && !selectedId) {
      setSelectedId(processedStock[0].id);
    }
  }, [processedStock, selectedId]);

  // Notify parent of stock check
  useDebouncedEffect(
    () => {
      if (stockCheckData) {
        handleStockCheck(stockCheckData.qtyOnHand > 0, stockCheckData);
      }
    },
    [stockCheckData, handleStockCheck],
    150,
  );

  // Notify parent of available colors
  useDebouncedEffect(
    () => {
      if (getColors) {
        getColors(colorDataForParent);
      }
    },
    [colorDataForParent, getColors],
    150,
  );

  // Memoized selection change handler
  const handleSelectionChange = useCallback((keys: Selection) => {
    const key = Array.from(keys)[0]?.toString();
    if (key) setSelectedId(key);
  }, []);

  // Memoized dropdown open change handler
  const handleDropdownOpenChange = useCallback((open: boolean) => {
    setIsDropdownOpen(open);
  }, []);

  // Memoized item click handler for mobile
  const createItemClickHandler = useCallback((itemId: string, onClose: () => void) => {
    return () => {
      setSelectedId(itemId);
      onClose();
    };
  }, []);


  // Mobile modal component
  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-2">
        <span className="font-semibold text-gray-700 dark:text-gray-200">
          1. Select Product Colour
        </span>
        <span className="text-sm text-gray-500 dark:text-gray-100 italic">Colours in Stock</span>
      </div>
      {isMobile ? (
        <>
          {/* Mobile UI */}
          <div
            className="w-full h-12 border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 p-3 rounded-lg cursor-pointer flex items-center justify-between shadow-sm"
            onClick={onOpen}
          >
            {selectedItem ? (
              <div className="flex items-center gap-3">
                <div
                  className="w-6 h-6 rounded-full border border-gray-100 shadow-sm"
                  style={{
                    backgroundColor: getColorHex(selectedItem.colour?.toLowerCase() || "") || "",
                  }}
                />
                <span className="font-medium">
                  {selectedItem.colour} {selectedItem.size}
                </span>
              </div>
            ) : (
              <span className="text-gray-500">Select a colour</span>
            )}
            <svg
              className="w-4 h-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>

          <Modal
            placement="bottom"
            scrollBehavior="inside"
            isOpen={isOpen}
            onOpenChange={onOpenChange}
            classNames={{
              base: "m-0 rounded-t-2xl rounded-b-none sm:rounded-xl",
            }}
          >
            <ModalContent>
              {(onClose) => (
                <>
                  <ModalHeader className="flex flex-col gap-1 border-b border-gray-100 dark:border-gray-700">
                    Select Product Colour
                  </ModalHeader>
                  <ModalBody className="p-0">
                    <div className="p-4 space-y-4">
                      {defaultColors.length > 0 && (
                        <div className="">
                          <div className="font-semibold text-sm text-gray-500 mb-2 uppercase tracking-wide">
                            Product Colours
                          </div>
                          <div className="space-y-1">
                            {defaultColors.map((item) => {
                              const hasDueDate = item.dueDate && item.dueDate !== "-";
                              const hasDateExpected =
                                item.dateExpected && item.dateExpected !== "-";
                              const hasOnOrder = item.onOrder && item.onOrder > 0;
                              const showMoreDue = hasDueDate || hasDateExpected || hasOnOrder;
                              return (
                                <div
                                  key={item.id}
                                  className={`flex items-center p-3 rounded-lg cursor-pointer transition-colors ${
                                    selectedId === item.id
                                      ? "bg-primary/10 border border-primary/20"
                                      : "hover:bg-gray-50 dark:hover:bg-gray-800"
                                  }`}
                                  onClick={() => {
                                    setSelectedId(item.id);
                                    onClose();
                                  }}
                                >
                                  <div
                                    className="w-8 h-8 rounded-full border border-gray-200 shadow-sm mr-3"
                                    style={{
                                      backgroundColor:
                                        getColorHex(item.colour?.toLowerCase() || "") || "",
                                    }}
                                  />
                                  <div className="flex flex-col">
                                    <span
                                      className={`font-medium ${
                                        selectedId === item.id ? "text-primary" : ""
                                      }`}
                                    >
                                      {item.colour} {item.size}
                                    </span>
                                    {item.quantity > 0 ? (
                                      <span className="text-xs text-green-600 flex items-center gap-1">
                                        <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div>
                                        {item.quantity} In Stock
                                      </span>
                                    ) : (
                                      <span className="text-xs text-red-500">Out of Stock</span>
                                    )}
                                    {showMoreDue ? (
                                      <span className="italic text-xs text-foreground-600 mt-0.5">
                                        {hasDueDate || hasDateExpected
                                          ? `More Due ${
                                              hasDueDate ? item.dueDate : item.dateExpected
                                            }`
                                          : "More Due"}
                                      </span>
                                    ) : null}
                                  </div>
                                  {selectedId === item.id && (
                                    <IoMdCheckmarkCircle className="ml-auto text-primary text-xl" />
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {addonColors.length > 0 && (
                        <div>
                          <div className="font-semibold text-sm text-gray-500 mt-4 mb-2 uppercase tracking-wide">
                            Accessories (Request Quote)
                          </div>
                          <div className="space-y-1">
                            {addonColors.map((item) => (
                              <div
                                key={item.id}
                                className={`flex items-center p-3 rounded-lg cursor-pointer transition-colors ${
                                  selectedId === item.id
                                    ? "bg-primary/10 border border-primary/20"
                                    : "hover:bg-gray-50 dark:hover:bg-gray-800"
                                }`}
                                onClick={() => {
                                  setSelectedId(item.id);
                                  onClose();
                                }}
                              >
                                <StockItemDisplay item={item} />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </ModalBody>
                  <ModalFooter className="border-t border-gray-100 dark:border-gray-700">
                    <Button fullWidth onPress={onClose}>
                      Cancel
                    </Button>
                  </ModalFooter>
                </>
              )}
            </ModalContent>
          </Modal>
        </>
      ) : (
        <Select
          variant="bordered"
          labelPlacement="outside"
          isVirtualized={true}
          isOpen={isDropdownOpen}
          onOpenChange={handleDropdownOpenChange}
          popoverProps={{
            isKeyboardDismissDisabled: true,
            isDismissable: true,
          }}
          itemHeight={56}
          placeholder="Select a colour"
          selectedKeys={new Set([selectedId])}
          onSelectionChange={handleSelectionChange}
          className="w-full"
          classNames={{
            trigger:
              "h-12 bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 hover:bg-zinc-50 shadow-sm",
            value: "font-medium text-lg",
          }}
          renderValue={() => {
            if (!selectedItem) return "Select a colour";
            return (
              <div className="flex items-center gap-3">
                <div
                  className="w-6 h-6 rounded-full border border-gray-200 shadow-sm"
                  style={{
                    backgroundColor: getColorHex(selectedItem.colour?.toLowerCase() || "") || "",
                  }}
                />
                <span>
                  {selectedItem.colour} {selectedItem.size}
                </span>
              </div>
            );
          }}
        >
          {defaultColors.length > 0 ? (
            <SelectSection title="Product Colours" items={defaultColors}>
              {(item) => {
                const hasDueDate = item.dueDate && item.dueDate !== "-";
                const hasDateExpected = item.dateExpected && item.dateExpected !== "-";
                const hasOnOrder = item.onOrder && item.onOrder > 0;
                const showMoreDue = hasDueDate || hasDateExpected || hasOnOrder;
                return (
                  <SelectItem key={item.id} textValue={item.colour || ""} className="min-h-14 py-2">
                    <div className="flex items-center justify-between w-full pr-2">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-8 h-8 rounded-full border border-gray-200 shadow-sm"
                          style={{
                            backgroundColor: getColorHex(item.colour?.toLowerCase() || "") || "",
                          }}
                        />
                        <div className="flex flex-col">
                          <span className="font-semibold text-small">
                            {item.colour} {item.size}
                          </span>
                          <span className="text-tiny text-gray-500">{item.sku}</span>
                        </div>
                      </div>
                      <div className="flex flex-col items-end text-right">
                        {item.quantity > 0 ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                            {item.quantity} In Stock
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                            Out of Stock
                          </span>
                        )}
                        {showMoreDue ? (
                          <span className="italic text-xs text-foreground-600 mt-0.5">
                            {hasDueDate || hasDateExpected
                              ? `More Due ${hasDueDate ? item.dueDate : item.dateExpected}`
                              : "More Due"}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </SelectItem>
                );
              }}
            </SelectSection>
          ) : null}

          {addonColors.length > 0 ? (
            <SelectSection
              title="Possible Product Accessories (Please Request a Quote)"
              items={addonColors}
            >
              {(item) => (
                <SelectItem key={item.id} textValue={item.colour || ""}>
                  <StockItemDisplay item={item} />
                </SelectItem>
              )}
            </SelectSection>
          ) : null}
        </Select>
      )}
    </div>
  );
};

// Memoized export
const MemoizedStockDataV2 = React.memo(StockDataV2);

export default MemoizedStockDataV2;
